import { GetServerSidePropsContext } from 'next';
import { ICommonServerSideProps } from '@belong/common-nextjs-server-side-props';
import { withBasePath } from '@belong/url-utils';
import { Entry } from 'contentful';
import { IPageData, IPageSchemaData } from 'src/core/types';

/**
 * Extracts schema data from page data for JSON-LD structured data.
 * @param pageData - The properties of the page.
 * @param context - The Next.js context object.
 * @param appServerSideProps - The server-side props from the app.
 * @param pageProps - The properties of the page.
 * @returns The schema data object.
 */
export const getSchemaData = (
  pageData: Entry<any> | undefined,
  context: GetServerSidePropsContext,
  appServerSideProps: ICommonServerSideProps,
  pageProps: IPageData
): IPageSchemaData => {
  const protocol = context?.req.headers['x-forwarded-proto'] || 'https';
  const siteUrl = `${protocol}://${context?.req.headers.host}`;
  const pathName = context?.resolvedUrl || '/';

  // Clean up the path name to remove leading slashes
  const cleanPath = pathName.replace(/^\//, '');
  const isHome = cleanPath === '';

  // Construct the page URL
  const pageUrl = isHome ? `${siteUrl}${pathName}` : `${siteUrl}${withBasePath(pathName)}`;

  // Get social links from app server-side props
  const { socialLinks } = appServerSideProps.footerMenuContent || { socialLinks: [] };

  // Extract page entry data
  const pageEntry = pageData;
  const { pageTitle, description } = pageEntry?.fields || {};
  const { createdAt, updatedAt } = pageEntry?.sys || {};

  const sections = pageProps?.sections || [];
  const breadcrumbs = pageProps?.breadcrumbs || [];

  const identifiers = {
    internet: {
      faqs: 'Internet-plans-greater-FAQs',
      /**
       * Content type, rather than identitifer as this is a fixed value and there will only every be a single
       * section with this content type on the page
       */
      sectionNbnPlanSummary: 'sectionNbnPlanSummary'
    },
    mobile: {
      faqs: 'Mobile:-Our-mobile-plans-greater-Frequently-asked-questions',
      plans: 'Mobile:-Our-mobile-plans-greater-Plan-summary'
    },
    'data-plans': {
      faqs: 'data-plans-greater-Frequently-asked-questions-section',
      plans: 'data-plans-greater-plan-summary-section'
    }
  };

  const identifier = identifiers[cleanPath];
  const faqSection = identifier ? sections.filter(section => section.identifier === identifier.faqs) : [];
  const plansSection = identifier ? sections.filter(section => section.identifier === identifier.plans) : [];
  const sectionNbnPlanSummary = identifier
    ? sections.find(section => section.contentType === identifier.sectionNbnPlanSummary)
    : undefined;

  // Nbn plans are not attached to a section in Contentful, so we need to check if we're on the internet page and transform the plan cards from props
  const isInternetPage = cleanPath === 'internet';
  const planCards = Array.isArray(pageProps?.planCards) ? pageProps.planCards : [];
  const schemaPlans = isInternetPage ? planCards : (plansSection?.[0]?.plans ?? []);

  return {
    siteUrl,
    socialLinks,
    pageUrl,
    pathName: isHome ? 'home' : cleanPath,
    title: pageTitle,
    description,
    datePublished: createdAt?.substring(0, 10),
    dateModified: updatedAt?.substring(0, 10),
    breadcrumbs,
    faqs: faqSection?.[0]?.accordionRows || [],
    plans: schemaPlans,
    sectionNbnPlanSummary
  };
};
